import {
  addDays, subDays, nextMonday, nextFriday, format, addWeeks,
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  subWeeks, subMonths,
} from 'date-fns'
import type { TaskPriority, TaskStatus } from './types'

export interface NLParseResult {
  cleanTitle: string
  dueDate?: string
  dueTime?: string
  priority?: TaskPriority
}

export interface NLFilterResult {
  searchQuery: string
  dateRange?: { start: string; end: string }
  priority?: TaskPriority
  status?: TaskStatus
}

const DATE_PATTERNS: { pattern: RegExp; resolve: () => Date }[] = [
  { pattern: /오늘/, resolve: () => new Date() },
  { pattern: /내일/, resolve: () => addDays(new Date(), 1) },
  { pattern: /모레/, resolve: () => addDays(new Date(), 2) },
  { pattern: /다음\s*주\s*월요일/, resolve: () => nextMonday(addWeeks(new Date(), 0)) },
  { pattern: /다음\s*주\s*금요일/, resolve: () => nextFriday(addWeeks(new Date(), 0)) },
  { pattern: /이번\s*주\s*금요일/, resolve: () => nextFriday(new Date()) },
  { pattern: /(\d+)일\s*후/, resolve: () => new Date() }, // Handled specially below
]

const TIME_PATTERNS: { pattern: RegExp; resolve: (match: RegExpMatchArray) => string }[] = [
  // "오후 3시 30분" → "15:30"
  { pattern: /오후\s*(\d{1,2})시\s*(\d{1,2})분/, resolve: (m) => {
    const h = parseInt(m[1], 10)
    return `${String(h === 12 ? 12 : h + 12).padStart(2, '0')}:${String(parseInt(m[2], 10)).padStart(2, '0')}`
  }},
  // "오전 9시 30분" → "09:30"
  { pattern: /오전\s*(\d{1,2})시\s*(\d{1,2})분/, resolve: (m) => {
    const h = parseInt(m[1], 10)
    return `${String(h === 12 ? 0 : h).padStart(2, '0')}:${String(parseInt(m[2], 10)).padStart(2, '0')}`
  }},
  // "오후 3시" → "15:00"
  { pattern: /오후\s*(\d{1,2})시/, resolve: (m) => {
    const h = parseInt(m[1], 10)
    return `${String(h === 12 ? 12 : h + 12).padStart(2, '0')}:00`
  }},
  // "오전 9시" → "09:00"
  { pattern: /오전\s*(\d{1,2})시/, resolve: (m) => {
    const h = parseInt(m[1], 10)
    return `${String(h === 12 ? 0 : h).padStart(2, '0')}:00`
  }},
  // "3시 30분" (no AM/PM — 1~6 assume PM, 7~11 assume AM)
  { pattern: /(\d{1,2})시\s*(\d{1,2})분/, resolve: (m) => {
    let h = parseInt(m[1], 10)
    if (h >= 1 && h <= 6) h += 12
    return `${String(h).padStart(2, '0')}:${String(parseInt(m[2], 10)).padStart(2, '0')}`
  }},
  // "3시" → "15:00"
  { pattern: /(\d{1,2})시/, resolve: (m) => {
    let h = parseInt(m[1], 10)
    if (h >= 1 && h <= 6) h += 12
    return `${String(h).padStart(2, '0')}:00`
  }},
  // "15:00" (24h format)
  { pattern: /(\d{1,2}):(\d{2})/, resolve: (m) => {
    const h = parseInt(m[1], 10)
    const min = parseInt(m[2], 10)
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
      return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
    }
    return ''
  }},
  // Named times
  { pattern: /아침/, resolve: () => '09:00' },
  { pattern: /점심/, resolve: () => '12:00' },
  { pattern: /저녁/, resolve: () => '18:00' },
  { pattern: /밤/, resolve: () => '21:00' },
]

const PRIORITY_PATTERNS: { pattern: RegExp; priority: TaskPriority }[] = [
  { pattern: /긴급|급한|급히|중요|높은\s*우선\s*순위/, priority: 'high' },
  { pattern: /보통/, priority: 'medium' },
  { pattern: /나중에|낮은/, priority: 'low' },
]

// --- Filter-specific patterns ---

const STATUS_PATTERNS: { pattern: RegExp; status: TaskStatus }[] = [
  { pattern: /완료한|완료된|끝난|끝낸|다\s*한/, status: 'completed' },
  { pattern: /미완료|안\s*한|남은|못\s*한|진행\s*중|안\s*끝난/, status: 'pending' },
]

const DATE_RANGE_PATTERNS: {
  pattern: RegExp
  resolve: () => { start: Date; end: Date }
}[] = [
  {
    pattern: /어제/,
    resolve: () => {
      const d = subDays(new Date(), 1)
      return { start: d, end: d }
    },
  },
  {
    pattern: /그저께|그제/,
    resolve: () => {
      const d = subDays(new Date(), 2)
      return { start: d, end: d }
    },
  },
  {
    pattern: /오늘/,
    resolve: () => {
      const d = new Date()
      return { start: d, end: d }
    },
  },
  {
    pattern: /이번\s*주/,
    resolve: () => ({
      start: startOfWeek(new Date(), { weekStartsOn: 1 }),
      end: endOfWeek(new Date(), { weekStartsOn: 1 }),
    }),
  },
  {
    pattern: /지난\s*주|저번\s*주/,
    resolve: () => {
      const prev = subWeeks(new Date(), 1)
      return {
        start: startOfWeek(prev, { weekStartsOn: 1 }),
        end: endOfWeek(prev, { weekStartsOn: 1 }),
      }
    },
  },
  {
    pattern: /이번\s*달/,
    resolve: () => ({
      start: startOfMonth(new Date()),
      end: endOfMonth(new Date()),
    }),
  },
  {
    pattern: /지난\s*달|저번\s*달/,
    resolve: () => {
      const prev = subMonths(new Date(), 1)
      return { start: startOfMonth(prev), end: endOfMonth(prev) }
    },
  },
  {
    pattern: /(\d+)일\s*전/,
    resolve: () => new Date() as never, // handled specially
  },
]

/**
 * Parse Korean natural language task input into structured data.
 * Does NOT use AI — pure local regex matching.
 */
export function parseNaturalLanguage(input: string): NLParseResult {
  let text = input.trim()
  let dueDate: string | undefined
  let priority: TaskPriority | undefined

  // Extract relative day patterns (e.g. "3일 후")
  const dayMatch = text.match(/(\d+)일\s*후/)
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10)
    if (days > 0 && days <= 365) {
      dueDate = format(addDays(new Date(), days), 'yyyy-MM-dd')
      text = text.replace(dayMatch[0], '').trim()
    }
  }

  // Extract date patterns
  if (!dueDate) {
    for (const { pattern, resolve } of DATE_PATTERNS) {
      if (pattern.source.includes('\\d+')) continue // Skip "N일 후" (already handled)
      const match = text.match(pattern)
      if (match) {
        dueDate = format(resolve(), 'yyyy-MM-dd')
        text = text.replace(match[0], '').trim()
        break
      }
    }
  }

  // Extract explicit date (YYYY-MM-DD or MM/DD)
  if (!dueDate) {
    const explicitDate = text.match(/(\d{4})[-./](\d{1,2})[-./](\d{1,2})/)
    if (explicitDate) {
      const year = parseInt(explicitDate[1], 10)
      const month = parseInt(explicitDate[2], 10)
      const day = parseInt(explicitDate[3], 10)
      // Validate ranges, then round-trip through a Date to reject month-specific
      // overflow (e.g. "2026-02-31") that would otherwise silently roll over.
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const probe = new Date(year, month - 1, day)
        const valid =
          probe.getFullYear() === year &&
          probe.getMonth() === month - 1 &&
          probe.getDate() === day
        if (valid) {
          dueDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
          text = text.replace(explicitDate[0], '').trim()
        }
        // else: invalid date — leave dueDate undefined and keep the matched
        // token in the title so the user's text is preserved.
      }
    }
  }

  // Extract "~까지" suffix attached to dates already parsed
  text = text.replace(/까지\s*/g, '').trim()

  // Extract time patterns
  let dueTime: string | undefined
  for (const { pattern, resolve } of TIME_PATTERNS) {
    const match = text.match(pattern)
    if (match) {
      const resolved = resolve(match)
      if (resolved) {
        dueTime = resolved
        text = text.replace(match[0], '').trim()
        break
      }
    }
  }

  // Extract priority
  for (const { pattern, priority: p } of PRIORITY_PATTERNS) {
    if (pattern.test(text)) {
      priority = p
      text = text.replace(pattern, '').trim()
      break
    }
  }

  // Clean up extra whitespace
  const cleanTitle = text.replace(/\s+/g, ' ').trim()

  return {
    cleanTitle: cleanTitle || input.trim(),
    dueDate,
    dueTime,
    priority,
  }
}

/**
 * Parse Korean natural language search/filter query into structured filter criteria.
 * e.g. "지난주에 완료한 높은 우선순위 작업" → { dateRange, status: 'completed', priority: 'high' }
 */
export function parseNaturalLanguageFilter(input: string): NLFilterResult {
  let text = input.trim()

  // Extract status
  let status: TaskStatus | undefined
  for (const { pattern, status: s } of STATUS_PATTERNS) {
    if (pattern.test(text)) {
      status = s
      text = text.replace(pattern, '').trim()
      break
    }
  }

  // Extract date range — special: "N일 전"
  let dateRange: { start: string; end: string } | undefined
  const dayAgoMatch = text.match(/(\d+)일\s*전/)
  if (dayAgoMatch) {
    const days = parseInt(dayAgoMatch[1], 10)
    if (days > 0 && days <= 365) {
      const d = subDays(new Date(), days)
      dateRange = { start: format(d, 'yyyy-MM-dd'), end: format(d, 'yyyy-MM-dd') }
      text = text.replace(dayAgoMatch[0], '').trim()
    }
  }

  if (!dateRange) {
    for (const { pattern, resolve } of DATE_RANGE_PATTERNS) {
      if (pattern.source.includes('\\d+')) continue // Skip "N일 전" (already handled)
      const match = text.match(pattern)
      if (match) {
        const range = resolve()
        dateRange = {
          start: format(range.start, 'yyyy-MM-dd'),
          end: format(range.end, 'yyyy-MM-dd'),
        }
        text = text.replace(match[0], '').trim()
        break
      }
    }
  }

  // Extract priority
  let priority: TaskPriority | undefined
  for (const { pattern, priority: p } of PRIORITY_PATTERNS) {
    if (pattern.test(text)) {
      priority = p
      text = text.replace(pattern, '').trim()
      break
    }
  }

  // Clean up filler particles
  text = text
    .replace(/에|의|을|를|한|인|된|으로/g, ' ')
    .replace(/작업|태스크|할\s*일/g, ' ')
    .replace(/우선\s*순위/g, '')
    .replace(/\s+/g, ' ')
    .trim()

  return {
    searchQuery: text,
    dateRange,
    priority,
    status,
  }
}
